import { Button } from '@fox/atoms/src/button/index';
import { MuiAlert, MuiAlertTitle } from '@fox/atoms/src/mui-alert/index';
import { TempDataContextRefProvider } from '@fox/atoms/src/temp-data-context/index';
import { tryParseJSONObject } from '@fox/atoms/utils/string';
import { IWorkBenchLayout } from '@fox/blocks/contracts/work-bench';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as React from 'react';
import styled from 'styled-components';
import { FORM_VALIDATION_ERROR_NAME } from '../form-block-types';
import { SwiftFoxSearchResult } from './blocks/create-person-block/shared-components/contracts';
import { getAllBlocksFromLayout } from './blocks/form-block/conditionals/helpers';
import { ISection } from './blocks/section-block/contracts';
import { UploadedFilesWithMeta } from './blocks/upload-files-block/contracts';
import { useFormErrorContext } from './form-error-context';
import { EventCapacityData, FormEventAPIHelpers, FormEventContextProvider } from './form-event-context';
import { FormFinancialAPIHelpers, FormFinancialContextProvider } from './form-financial-context';
import { FormDefinitionResponse, IQuestionAnswer } from './form-root/contracts';
import { FormRuntime } from './form-root/form-root';
import {
	FormRenderMode,
	FormRuntimeContextProvider,
	QuestionStyles,
	SurveyState,
	SurveyTargetInfo,
	useFormRuntimeContext
} from './form-runtime-context';

export const formQueryClient = new QueryClient();

const SurveyBorderWrapper = styled.div<{ $showBorder?: boolean }>`
	.canvasWrapper {
		border: ${props => (props.$showBorder ? `1px solid ${props.theme.colors.grey?.[200]}` : 'none')};
		border-radius: ${props => (props.$showBorder ? '5px' : 'none')};
		overflow: hidden;
		max-height: max-content;
	}
`;

interface ISurveyProps {
	formRequestId: string;
	formDef: FormDefinitionResponse;
	metadata?: any;
	onSubmit: (answers: IQuestionAnswer[], formTarget?: SurveyTargetInfo) => Promise<any>;
	onSubmitted: (res: any) => any;
	financialApiHelpers: FormFinancialAPIHelpers;
	/**When using forms within SwiftFox, we do not need a token. Some api calls are blocked when a token has not been verified, this allows us to make those calls regardless */
	inLocalSurveyMode?: boolean;
	showBorder?: boolean;
	isPreviewMode?: boolean;
	initialAnswers?: IQuestionAnswer[];
	submitButtonLabel?: string;
	submitButtonDisabled?: boolean;
	token?: string;
	preValidatedFormToken?: string;
	eventCapacityData?: EventCapacityData;
	surveyState?: SurveyState;
	surveyResponse?: string;
	targetInfo?: SurveyTargetInfo;
	eventApiHelpers: FormEventAPIHelpers;
	getPointsTotal: (input: {
		sections: ISection[];
		layout: IWorkBenchLayout | undefined;
		answers: IQuestionAnswer[];
		formId?: number;
	}) => Promise<{ status: 'success' | 'error'; pointTotal?: number; message?: string }>;
	complete: (input: { searchString: string; types?: string[]; categories?: string[]; signedParams?: string }) => Promise<SwiftFoxSearchResult[]>;
	uploadFile: (input: { file: File }) => Promise<UploadedFilesWithMeta | undefined>;
	styleOverrides?: Partial<QuestionStyles>;
}

export const SurveyErrors = ({ surveyState, surveyResponse, formLayout }: { surveyState?: SurveyState; surveyResponse?: string; formLayout }) => {
	const { errors } = useFormErrorContext() ?? {};
	const { currentPage } = useFormRuntimeContext();

	const allBlocks = getAllBlocksFromLayout(formLayout);
	const sections = formLayout.layouts[0].props.sections;
	// TODO : Filter out invalid conditionals too
	const filteredErrors = Object.entries(errors ?? {}).reduce(
		(acc, [name, err]) => {
			const matchingBlock = allBlocks.find(b => b.name === name);
			const friendlyName = matchingBlock?.title ?? matchingBlock?.type ?? name;
			const pageNo = sections.findIndex(s => s.fields.find(f => f.name === matchingBlock?.name));

			// Only show errors on current or last page
			if (
				friendlyName === FORM_VALIDATION_ERROR_NAME ||
				(typeof currentPage === 'number' && (pageNo === currentPage || currentPage === sections.length - 1))
			) {
				acc.push({ ...err, name: friendlyName });
			}
			return acc;
		},
		[] as { name: string; message?: string | null; setPage?: () => void }[]
	);

	const errorLength = filteredErrors.length;

	if (surveyState !== SurveyState.error && errorLength === 0) return null;
	return (
		<>
			<MuiAlert style={{ maxWidth: '960px', margin: 'auto' }} severity="error">
				<MuiAlertTitle>Error</MuiAlertTitle>
				{surveyResponse}
				{errorLength > 0 ? (
					<ul>
						{filteredErrors.map(error => {
							return (
								<li key={error.name}>
									<Button variant="tertiary" color="error" onClick={error.setPage}>
										{`${error.name}: ${error.message}`}
									</Button>
								</li>
							);
						})}
					</ul>
				) : null}
			</MuiAlert>
		</>
	);
};

function applyStyleOverride(overrideStyles: Partial<QuestionStyles> | undefined, styles: QuestionStyles): QuestionStyles {
	const merge = <T extends object>(base: T, override?: Partial<T>): T => ({
		...base,
		...(override ?? {})
	});

	return {
		generalFormStyle: merge(styles?.generalFormStyle ?? {}, overrideStyles?.generalFormStyle),
		questionAnswerStyle: merge(styles?.questionAnswerStyle ?? {}, overrideStyles?.questionAnswerStyle),
		questionDescriptionStyle: merge(styles?.questionDescriptionStyle ?? {}, overrideStyles?.questionDescriptionStyle),
		questionLabelStyle: merge(styles?.questionLabelStyle ?? {}, overrideStyles?.questionLabelStyle)
	};
}

// Public entry point for a survey
export const Survey = ({
	initialAnswers,
	formDef,
	onSubmit,
	onSubmitted,
	showBorder,
	submitButtonDisabled,
	submitButtonLabel,
	eventCapacityData,
	token,
	preValidatedFormToken,
	surveyState,
	surveyResponse,
	targetInfo,
	financialApiHelpers,
	inLocalSurveyMode,
	eventApiHelpers,
	getPointsTotal,
	complete,
	uploadFile,
	styleOverrides,
	formRequestId
}: ISurveyProps) => {
	const tempDataRef = React.useRef<Record<string, any>>({});
	const layout = tryParseJSONObject<IWorkBenchLayout>(formDef.layout);
	const styles: QuestionStyles = layout?.layouts?.[0]?.props?.innerLayout?.styles;
	const stylesToUse = styleOverrides ? applyStyleOverride(styleOverrides, styles) : styles;

	(window as any).validatedFormToken = token;
	(window as any).preValidatedFormToken = preValidatedFormToken;

	if (!layout) {
		return null;
	}

	return (
		<QueryClientProvider client={formQueryClient}>
			<TempDataContextRefProvider data={tempDataRef.current}>
				<FormFinancialContextProvider {...financialApiHelpers}>
					<FormEventContextProvider
						formRequestId={formRequestId}
						token={token}
						ownerId={formDef.ownerId}
						event={eventCapacityData}
						{...eventApiHelpers}
					>
						<FormRuntimeContextProvider
							formId={formDef.id}
							formRequestId={formRequestId}
							submitButtonLabel={submitButtonLabel}
							layout={layout}
							styles={stylesToUse}
							initialAnswers={initialAnswers}
							targetInfo={targetInfo}
							ownerId={formDef.ownerId}
							ownerType={formDef.ownerType}
							onSubmit={onSubmit}
							onSubmitted={onSubmitted}
							submitButtonDisabled={submitButtonDisabled}
							getPointsTotal={getPointsTotal}
							complete={complete}
							surveyState={surveyState}
							inLocalSurveyMode={inLocalSurveyMode}
							formRenderMode={FormRenderMode.runtime}
							uploadFile={uploadFile}
						>
							<SurveyBorderWrapper $showBorder={showBorder}>
								<FormRuntime token={token} />
								<SurveyErrors surveyResponse={surveyResponse} surveyState={surveyState} formLayout={layout} />
							</SurveyBorderWrapper>
						</FormRuntimeContextProvider>
					</FormEventContextProvider>
				</FormFinancialContextProvider>
			</TempDataContextRefProvider>
		</QueryClientProvider>
	);
};

export default Survey;
